import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, InputAdornment, Typography, Stack, Button, FormControl } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Add, ManageSearch, Launch, LinkOff, Link as LinkIcon } from '@mui/icons-material'
import store from 'state/store'
import { showFormModal } from 'state/ui'
import FORM_MODALS from 'utils/ui/modals'
import Select from 'components/generic/form/Select'
import Input from 'components/generic/form/Input'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import { CUSTOMERS_FILTERS, GS_ERROR_CODE } from 'utils/constants'
import { useTranslation } from 'react-i18next'
import { dispatchSnackbar } from 'utils/helpers'
import { usePatchDispatchTicketMutation } from 'services/dispatch'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import { getCustomerId } from 'components/containers/inbound-tickets/utils.js'
import { useTheme } from '@mui/styles'
import SkeletonWrapper from 'components/generic/SkeletonWrapper'

const pickupOptions = [
  {
    id: 'false',
    name: 'No',
  },
  {
    id: 'true',
    name: 'Yes',
  },
]

const DispatchTicketFormArea = ({
  dispatchTicketData,
  ibtData,
  disabled,
  statusIsPaidOrVoided,
  dispatch,
  setShowUnlinkModal,
  formData,
  materialTypesData,
  isLoading,
}) => {
  const theme = useTheme()

  if (!dispatchTicketData) {
    return (
      <SkeletonWrapper isLoading={isLoading}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Button
            variant="outlined"
            disabled={disabled || statusIsPaidOrVoided()}
            sx={{ flex: 1, border: '1px solid #009F4D80', fontWeight: 'bold' }}
            startIcon={<LinkIcon />}
            onClick={() => {
              dispatch(
                showFormModal({
                  formModalName: FORM_MODALS.CREATE_FROM_DISPATCH,
                  formModalProps: {
                    ticketId: ibtData?.id,
                    billId: ibtData?.billId,
                    ticketNumber: ibtData?.ticketId,
                    customerId: getCustomerId(null, ibtData, null, null),
                    inboundTicketData: ibtData,
                    materialTypesData,
                    formData: formData,
                  },
                }),
              )
            }}
          >
            Link Dispatch Ticket
          </Button>
        </Box>
      </SkeletonWrapper>
    )
  } else {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
        <FormControl sx={{ flex: 1 }}>
          <Typography
            variant="body1"
            style={{
              // This manually matches the <InputLabel shrink/> styling
              position: 'absolute',
              top: 0,
              left: 0,
              fontSize: '0.75rem',
              color: theme.palette.text.secondary,
              lineHeight: '1.5',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              transform: 'translateY(-10px)',
            }}
          >
            Dispatch Ticket
          </Typography>
          <Typography
            variant="body1"
            style={{
              marginTop: '6px',
              textAlign: 'left',
            }}
          >
            #{dispatchTicketData.externalId}
          </Typography>
        </FormControl>
        <IconButton sx={{ borderRadius: 1 }} onClick={() => setShowUnlinkModal(true)}>
          <LinkOff />
        </IconButton>
        <IconButton
          sx={{ borderRadius: 1 }}
          onClick={() => {
            window.open('/app/load-board/ticket-history/' + dispatchTicketData.id, '_blank')
          }}
        >
          <Launch />
        </IconButton>
      </Box>
    )
  }
}

const TransportationInformation = ({
  disabled,
  freightBillIsPaid,
  ibtData,
  dispatchTicketData,
  statusIsPaidOrVoided,
  formatData,
  materialTypesData,
  isLoading,
}) => {
  const methods = useFormContext()
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
  } = methods
  const dispatch = useDispatch()
  const [patchDispatchTicket] = usePatchDispatchTicketMutation()
  const { t } = useTranslation('inboundTicket')

  const purchaseOrderShouldDirty = useRef(false)
  const { purchaseOrderNumber, purchaseOrderId } = useSelector(state => state.inboundTicket.transportationInformation)
  const { appRouteLoadBoard } = useSelector(state => state.featureFlags?.variables || {})
  const { useMinimalObt } = useSelector(state => state.featureFlags?.variables || {})

  const [showUnlinkModal, setShowUnlinkModal] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)

  const handleUnlinkDispatchTicket = async () => {
    setIsUnlinking(true)

    await patchDispatchTicket({
      patchDispatchTicketBody: {
        inboundTicketId: null,
        hash: dispatchTicketData.hash,
      },
      dispatchTicketId: dispatchTicketData.id,
      inboundTicketId: ibtData.id,
      assetIds: [],
    })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Dispatch Ticket successfully unlinked.')
      })
      .catch(error => {
        let errorMessage = 'There was an error unlinking the Dispatch Ticket.'

        if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
          errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
        }

        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
      .finally(() => {
        setShowUnlinkModal(false)
        setIsUnlinking(false)
      })
  }

  useEffect(() => {
    if (purchaseOrderId || purchaseOrderId === '') {
      setValue('transportationInfo.purchaseOrderNumber', purchaseOrderNumber || '', {
        shouldDirty: purchaseOrderShouldDirty.current,
      })
      setValue('transportationInfo.purchaseOrderId', purchaseOrderId || '', {
        shouldDirty: purchaseOrderShouldDirty.current,
      })
      setValue('transportationInfo.hasEditedPurchaseOrder', purchaseOrderShouldDirty.current, {
        shouldDirty: purchaseOrderShouldDirty.current,
      })
    }
  }, [purchaseOrderId])

  return (
    <>
      {showUnlinkModal && (
        <AlertModal
          open={showUnlinkModal}
          modalType="payment"
          title="Unlink Dispatch Ticket"
          question={`Are you sure you want to unlink Dispatch Ticket #${dispatchTicketData?.externalId}?`}
          confirmLabel="UNLINK DISPATCH TICKET"
          cancelLabel="CANCEL"
          description=""
          onClose={() => {
            setShowUnlinkModal(false)
          }}
          onSubmit={handleUnlinkDispatchTicket}
          showIsLoading={isUnlinking}
        />
      )}
      <Box width={{ xs: '100%', md: '50%' }} data-testid="transportation-info">
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
          <Typography variant="h6" gutterBottom color="textSecondary">
            {t('transportation_info')}
          </Typography>
          {!isLoading && !disabled && !useMinimalObt && (
            <Button
              id="add-vehicle-button"
              size="small"
              variant="text"
              disabled={disabled || statusIsPaidOrVoided()}
              onClick={() =>
                store.dispatch(
                  showFormModal({
                    formModalName: FORM_MODALS.ADD_VEHICLE,
                    formModalProps: {
                      tab: 'scrap-vehicle',
                    },
                  }),
                )
              }
              startIcon={<Add />}
            >
              {t('add_vehicle')}
            </Button>
          )}
        </Stack>
        <Stack
          paddingX={2}
          paddingTop={useMinimalObt ? 0 : 2}
          spacing={2}
          paddingBottom={2}
          sx={{
            backgroundColor: 'background.section',
            borderRadius: '8px',
          }}
        >
          <Stack direction="row" spacing={2}>
            {!useMinimalObt && (
              <SkeletonWrapper isLoading={isLoading}>
                <Select
                  name="transportationInfo.isPickup"
                  label={t('pickup')}
                  variant="outlined"
                  size="small"
                  disabled={disabled || statusIsPaidOrVoided()}
                  defaultValue={pickupOptions[0].id}
                  control={control}
                  items={pickupOptions}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ flex: 1 }}
                />
              </SkeletonWrapper>
            )}
            {!useMinimalObt && (
              <SkeletonWrapper isLoading={isLoading}>
                <Input
                  name="transportationInfo.purchaseOrderNumber"
                  label={t('purchase_order')}
                  onClick={() => {
                    if (disabled || statusIsPaidOrVoided()) {
                      return
                    }
                    purchaseOrderShouldDirty.current = true
                    store.dispatch(
                      showFormModal({
                        formModalName: FORM_MODALS.LINK_PURCHASE_ORDER,
                      }),
                    )
                  }}
                  data-test="transportation-info-purchase-order-number"
                  variant="outlined"
                  size="small"
                  defaultValue=""
                  disabled={disabled || statusIsPaidOrVoided()}
                  control={control}
                  error={!!errors.transportationInfo?.purchaseOrderNumber}
                  helperText={errors.transportationInfo?.purchaseOrderNumber?.message}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton disabled={disabled || statusIsPaidOrVoided()}>
                          <ManageSearch color={disabled || statusIsPaidOrVoided() ? 'unset' : 'primary'} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              </SkeletonWrapper>
            )}
            {appRouteLoadBoard && !useMinimalObt && (
              <DispatchTicketFormArea
                dispatchTicketData={dispatchTicketData}
                ibtData={ibtData}
                disabled={disabled}
                statusIsPaidOrVoided={statusIsPaidOrVoided}
                dispatch={dispatch}
                setShowUnlinkModal={setShowUnlinkModal}
                formData={formatData(getValues())}
                materialTypesData={materialTypesData}
                isLoading={isLoading}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={2}>
            {!useMinimalObt && (
              <SkeletonWrapper isLoading={isLoading}>
                <Input
                  name="transportationInfo.billOfLading"
                  label={t('bill_of_lading')}
                  data-test="transportation-info-bof"
                  variant="outlined"
                  size="small"
                  defaultValue=""
                  disabled={disabled}
                  control={control}
                  error={!!errors.transportationInfo?.billOfLading}
                  helperText={errors.transportationInfo?.billOfLading?.message}
                />
              </SkeletonWrapper>
            )}
            <SkeletonWrapper isLoading={isLoading}>
              <Input
                name="transportationInfo.driverName"
                label={t('driver_name')}
                data-test="transportation-info-driver-name"
                variant="outlined"
                size="small"
                defaultValue=""
                disabled={disabled}
                control={control}
                error={!!errors.transportationInfo?.driverName}
                helperText={errors.transportationInfo?.driverName?.message}
              />
            </SkeletonWrapper>
            <SkeletonWrapper isLoading={isLoading}>
              <CustomerAutocomplete
                name="transportationInfo.carrierLookup"
                label={t('carrier')}
                data-test="transportation-info-carrier-name"
                variant="outlined"
                size="small"
                defaultValue={null}
                disabled={disabled || freightBillIsPaid}
                control={control}
                error={!!errors.transportationInfo?.carrierLookup}
                helperText={errors.transportationInfo?.carrierLookup?.message}
                fullWidth
                freeSolo={false}
                baseFilter={CUSTOMERS_FILTERS.CARRIER}
                autocompleteProps={{
                  renderOption: (props, option) => (
                    <li {...props} key={option.id}>
                      {option.displayName}
                    </li>
                  ),
                  isOptionEqualToValue: (option, value) => option?.id === value?.id,
                }}
              />
            </SkeletonWrapper>
          </Stack>
          <Stack direction="row" spacing={2}>
            <SkeletonWrapper isLoading={isLoading}>
              <Input
                name="transportationInfo.carrierNumber"
                label={t('truck_number')}
                data-test="transportation-info-carrier-number"
                variant="outlined"
                size="small"
                defaultValue=""
                disabled={disabled}
                control={control}
                error={!!errors.transportationInfo?.carrierNumber}
                helperText={errors.transportationInfo?.carrierNumber?.message}
              />
            </SkeletonWrapper>
            <SkeletonWrapper isLoading={isLoading}>
              <Input
                name="transportationInfo.trailerNumber"
                label={t('trailer_number')}
                data-test="transportation-info-trailer-number"
                variant="outlined"
                size="small"
                defaultValue=""
                disabled={disabled}
                control={control}
                error={!!errors.transportationInfo?.trailerNumber}
                helperText={errors.transportationInfo?.trailerNumber?.message}
              />
            </SkeletonWrapper>
            {!useMinimalObt && (
              <SkeletonWrapper isLoading={isLoading}>
                <Input
                  name="transportationInfo.containerNumber"
                  label={t('container_number')}
                  data-test="transportation-info-container-number"
                  variant="outlined"
                  size="small"
                  defaultValue=""
                  disabled={disabled}
                  control={control}
                  error={!!errors.transportationInfo?.containerNumber}
                  helperText={errors.transportationInfo?.containerNumber?.message}
                />
              </SkeletonWrapper>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  )
}

TransportationInformation.propTypes = {
  disabled: PropTypes.bool,
  freightBillIsPaid: PropTypes.bool,
  isLoading: PropTypes.bool,
}

TransportationInformation.defaultProps = {
  disabled: false,
  freightBillIsPaid: false,
  isLoading: false,
}

export default TransportationInformation
